import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaPause,
  FaPlay,
  FaVolumeUp,
} from "react-icons/fa";

import { getPublicAlert } from "../services/settingsServices";
import Reveal from "./ui/Reveal";

const TYPE_META = {
  info: {
    label: "Information",
    eyebrow: "Information municipale",
    icon: FaInfoCircle,
    border: "border-l-sky-500",
    badge: "bg-sky-500",
    iconBox: "bg-sky-500/12 text-sky-600 dark:text-sky-300",
    cardGradient:
      "from-sky-50 via-white to-slate-50 dark:from-sky-950/35 dark:via-slate-900 dark:to-slate-950",
    glow: "bg-sky-400/20",
    button:
      "bg-sky-600 text-white hover:bg-sky-700 shadow-sky-950/20 dark:bg-sky-500 dark:hover:bg-sky-400",
  },
  warning: {
    label: "Alerte",
    eyebrow: "À lire attentivement",
    icon: FaExclamationTriangle,
    border: "border-l-amber-500",
    badge: "bg-amber-500",
    iconBox: "bg-amber-500/12 text-amber-600 dark:text-amber-300",
    cardGradient:
      "from-amber-50 via-white to-slate-50 dark:from-amber-950/35 dark:via-slate-900 dark:to-slate-950",
    glow: "bg-amber-400/20",
    button:
      "bg-amber-500 text-white hover:bg-amber-600 shadow-amber-950/20 dark:bg-amber-500 dark:hover:bg-amber-400",
  },
  danger: {
    label: "Urgence",
    eyebrow: "Message urgent",
    icon: FaExclamationTriangle,
    border: "border-l-rose-500",
    badge: "bg-rose-500",
    iconBox: "bg-rose-500/12 text-rose-600 dark:text-rose-300",
    cardGradient:
      "from-rose-50 via-white to-slate-50 dark:from-rose-950/35 dark:via-slate-900 dark:to-slate-950",
    glow: "bg-rose-400/20",
    button:
      "bg-rose-600 text-white hover:bg-rose-700 shadow-rose-950/20 dark:bg-rose-500 dark:hover:bg-rose-400",
  },
  success: {
    label: "Info",
    eyebrow: "Bonne nouvelle",
    icon: FaCheckCircle,
    border: "border-l-emerald-500",
    badge: "bg-emerald-500",
    iconBox: "bg-emerald-500/12 text-emerald-600 dark:text-emerald-300",
    cardGradient:
      "from-emerald-50 via-white to-slate-50 dark:from-emerald-950/35 dark:via-slate-900 dark:to-slate-950",
    glow: "bg-emerald-400/20",
    button:
      "bg-emerald-600 text-white hover:bg-emerald-700 shadow-emerald-950/20 dark:bg-emerald-500 dark:hover:bg-emerald-400",
  },
};

const safeStr = (value) => (typeof value === "string" ? value.trim() : "");

const isAbsoluteUrl = (href = "") => /^https?:\/\//i.test(href);

function AlertLink({ href, children, className = "" }) {
  if (!href) return null;

  if (isAbsoluteUrl(href)) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={className}>
        {children}
      </a>
    );
  }

  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  );
}

function AudioReadButton({ title, message }) {
  const [isSpeaking, setIsSpeaking] = useState(false);

  const canSpeak =
    typeof window !== "undefined" &&
    "speechSynthesis" in window &&
    typeof window.SpeechSynthesisUtterance !== "undefined";

  useEffect(() => {
    return () => {
      if (canSpeak) {
        window.speechSynthesis.cancel();
      }
    };
  }, [canSpeak]);

  const handleRead = () => {
    if (!canSpeak) return;

    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    const textToRead = [title, message].filter(Boolean).join(". ");

    if (!textToRead.trim()) return;

    const utterance = new window.SpeechSynthesisUtterance(textToRead);
    utterance.lang = "fr-FR";
    utterance.rate = 0.95;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    window.speechSynthesis.cancel();
    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };

  if (!canSpeak) return null;

  return (
    <button
      type="button"
      onClick={handleRead}
      aria-label={isSpeaking ? "Arrêter la lecture audio" : "Lire le message"}
      title={isSpeaking ? "Arrêter la lecture audio" : "Lire le message"}
      className="group inline-flex min-h-8 items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-700 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
    >
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-sky-100 text-sky-700 transition group-hover:bg-sky-600 group-hover:text-white dark:bg-sky-500/15 dark:text-sky-300">
        {isSpeaking ? (
          <FaPause className="h-2.5 w-2.5" />
        ) : (
          <FaPlay className="h-2.5 w-2.5 translate-x-[1px]" />
        )}
      </span>

      <span>{isSpeaking ? "Arrêter" : "Écouter"}</span>

      <FaVolumeUp className="hidden h-3 w-3 opacity-60 sm:block" />
    </button>
  );
}

export default function HomeAlertBanner() {
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchAlert = async () => {
      try {
        const response = await getPublicAlert();

        if (!mounted) return;

        setAlert(response?.alert || null);
      } catch (error) {
        console.error("Erreur getPublicAlert:", error);

        if (mounted) {
          setAlert(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchAlert();

    return () => {
      mounted = false;
    };
  }, []);

  const computed = useMemo(() => {
    if (!alert) return null;

    return {
      isActive: Boolean(alert.is_active),
      type: safeStr(alert.type) || "info",
      title: safeStr(alert.title),
      message: safeStr(alert.message),
      linkLabel: safeStr(alert.link_label),
      linkUrl: safeStr(alert.link_url),
    };
  }, [alert]);

  if (loading) return null;
  if (!computed?.isActive) return null;
  if (!computed.title && !computed.message) return null;

  const meta = TYPE_META[computed.type] || TYPE_META.info;
  const Icon = meta.icon;

  const titleToDisplay = computed.title || "Information à la une";

  return (
    <section className="relative bg-slate-50 px-4 py-8 dark:bg-slate-950 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1380px]">
        <Reveal>
          <article
            className={`group relative overflow-hidden rounded-[2rem] border border-slate-200 border-l-[10px] ${meta.border} bg-gradient-to-r ${meta.cardGradient} shadow-xl shadow-slate-950/5 transition duration-300 hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-slate-950/10 dark:border-slate-800`}
          >
            <div
              className={`pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full ${meta.glow} blur-3xl transition duration-300 group-hover:scale-125`}
            />

            <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent dark:via-white/20" />

            <div className="relative grid gap-5 p-5 md:grid-cols-[auto_1fr_auto] md:items-center md:p-6 lg:p-7">
              <div
                className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${meta.iconBox} shadow-sm`}
              >
                <Icon className="h-6 w-6" />
              </div>

              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-600 shadow-sm ring-1 ring-slate-200 dark:bg-white/5 dark:text-slate-300 dark:ring-white/10">
                    <span className={`h-2 w-2 rounded-full ${meta.badge}`} />
                    {meta.eyebrow}
                  </span>

                  <span className="hidden text-xs font-bold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500 sm:inline">
                    Message officiel
                  </span>

                  <AudioReadButton
                    title={titleToDisplay}
                    message={computed.message}
                  />
                </div>

                <h2 className="mt-3 text-xl font-bold leading-tight text-slate-950 dark:text-white md:text-2xl">
                  {titleToDisplay}
                </h2>

                {computed.message ? (
                  <p className="mt-2 whitespace-pre-wrap break-words text-base font-medium leading-7 text-slate-600 dark:text-slate-300">
                    {computed.message}
                  </p>
                ) : null}
              </div>

              {computed.linkLabel && computed.linkUrl ? (
                <AlertLink
                  href={computed.linkUrl}
                  className={`inline-flex min-h-12 shrink-0 items-center justify-center gap-3 rounded-2xl px-5 py-3 text-sm font-bold shadow-lg transition duration-300 hover:-translate-y-0.5 ${meta.button}`}
                >
                  {computed.linkLabel}
                  <FaArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </AlertLink>
              ) : null}
            </div>

            <div className="relative border-t border-slate-200/80 bg-white/45 px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 backdrop-blur dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400 md:px-6">
              <span className="inline-flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${meta.badge}`} />
                {meta.label}
              </span>
            </div>
          </article>
        </Reveal>
      </div>
    </section>
  );
}